import React, {
  useCallback,
  useState,
  useRef,
  useContext,
  PropsWithChildren,
  useEffect,
} from "react"
import {
  ValidationProblemDetails,
  ProblemDetails,
} from "src/utilities/httpClients/gatewayClient"
import { useAbortController } from "./useAbortController"
import { useTranslation } from "src/i18next"
import { TFunction } from "i18next"
import { shallowEqualObjects } from "src/utilities/shallowEqualObjects"
import {
  isApiException,
  isProblemDetails,
  isValidationProblemDetails,
  toUnknownErrorMessage,
} from "src/utilities/httpClients/gatewayClientHelpers"
import { translateError } from "src/utilities/translateError"
import { useSetErrorMessage } from "src/providers/errorProvider"

const valid = "valid"

const customError = "customError"

type ValidityStateMap = Omit<
  {
    [K in keyof ValidityState]: string
  },
  typeof valid | typeof customError
>

type CompleteValidityStateMap = ValidityStateMap & {
  defaultErrorMessage: string
}

type Errors<T> = Partial<{ [K in keyof T]: string }>

export type ErrorMap<T> = Partial<{
  [K in keyof T]: Partial<ValidityStateMap>
}>

type UseForm<T> = {
  initiateState(): T
  request(req: T, abortSignal: AbortSignal): Promise<false | void>
  t: TFunction
  errorMapping?: ErrorMap<T>
}

export type HandleChange<T> = {
  (g: React.ChangeEvent<HTMLInputElement>): void
  (g: keyof T, value: string | boolean | null): void
}

export const eventToFieldAndValue =
  (onChange: (fieldName: string, value: string | boolean) => void) =>
  (ev: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = ev.target.name
    const value =
      ev.target.type === "checkbox" ? ev.target.checked : ev.target.value
    onChange(fieldName, value)
  }

const isKeyInStateInvalid = (
  key: string,
  validity: ValidityState,
): key is keyof ValidityStateMap => {
  return (
    key !== valid &&
    key !== customError &&
    validity[key as keyof ValidityState] === true
  )
}

const getErrorMessage = (
  errorMap: CompleteValidityStateMap,
  validity: ValidityState,
) => {
  for (const key in validity) {
    if (isKeyInStateInvalid(key, validity)) {
      const errorMessage = errorMap[key]

      if (errorMessage) return errorMessage
    }
  }
  return errorMap.defaultErrorMessage
}

const isFormElement = (
  inputEl: unknown,
): inputEl is HTMLInputElement | HTMLSelectElement => {
  return (
    inputEl != null &&
    (inputEl instanceof HTMLInputElement ||
      inputEl instanceof HTMLSelectElement)
  )
}

const errorMap =
  <T extends unknown>(
    defaultErrorMap: CompleteValidityStateMap,
    errorMappings: ErrorMap<T>,
  ) =>
  (invalidProperty: keyof T, validityState: ValidityState) => {
    const customErrorMap = errorMappings[invalidProperty] ?? {}

    return getErrorMessage(
      { ...defaultErrorMap, ...customErrorMap },
      validityState,
    )
  }

export const useForm = <T extends {}>({
  initiateState,
  request,
  t: _t,
  errorMapping = {},
}: UseForm<T>): FormMethods<T> => {
  const { t } = useTranslation("common")

  const translator = useCallback(
    (value: string) => {
      const defaultError = t("localizedStrings.errorMessages.webSiteProblem")
      return translateError(_t, defaultError)(value)
    },
    [_t, t],
  )

  const { setErrorMessage } = useSetErrorMessage()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [isDirty, setIsDirty] = useState(false)

  const hasSubmitted = useRef(false)

  const blurred = useRef<string[]>([])

  const formRef = useRef<HTMLFormElement>(null)

  const [state, setState] = useState<T>(initiateState)

  const [originalState, setOriginalState] = useState<T>(initiateState)

  const [errors, setErrors] = useState<Errors<T>>(() => ({}))

  const displayUnknownError = useCallback(
    (error?: string) => {
      setErrorMessage(
        error ?? t("localizedStrings.errorMessages.webSiteProblem"),
      )
    },
    [t, setErrorMessage],
  )

  const onInvalid = useCallback(
    (ev: React.FormEvent<HTMLFormElement>) => {
      const errorMessages = {
        badInput: t("localizedStrings.errorMessages.badInput"),
        patternMismatch: t("localizedStrings.errorMessages.patternMismatch"),
        rangeOverflow: t("localizedStrings.errorMessages.rangeOverflow"),
        rangeUnderflow: t("localizedStrings.errorMessages.rangeUnderflow"),
        stepMismatch: t("localizedStrings.errorMessages.stepMismatch"),
        tooLong: t("localizedStrings.errorMessages.tooLong"),
        tooShort: t("localizedStrings.errorMessages.tooShort"),
        typeMismatch: t("localizedStrings.errorMessages.typeMismatch"),
        valueMissing: t("localizedStrings.errorMessages.valueMissing"),
        defaultErrorMessage: t(
          "localizedStrings.errorMessages.defaultErrorMessage",
        ),
      }

      const el = ev.target
      if (isFormElement(el)) {
        if (!hasSubmitted.current && !blurred.current.includes(el.name)) return

        const completeErrorMap = errorMap(errorMessages, errorMapping)

        if (!el.validity.valid) {
          const errorMessage = el.validity.customError
            ? el.validationMessage
            : completeErrorMap(el.name as keyof T, el.validity)
          setErrors({
            ...errors,
            [el.name]: errorMessage,
          })
        }
      }
    },
    [errorMapping, errors, t],
  )

  const onReset = useCallback(() => {
    setState(originalState)
    setErrors({})
  }, [originalState])

  const onChange = useCallback(
    (
      f: string | React.ChangeEvent<HTMLInputElement>,
      v: string | boolean | undefined,
    ) => {
      const [fieldName, value] =
        typeof f === "string"
          ? [f, v]
          : [
              f.target.name,
              f.target.type === "checkbox" ? f.target.checked : f.target.value,
            ]

      setState({ ...state, [fieldName]: value })

      const inputEl = formRef?.current?.elements.namedItem(fieldName)

      if (isFormElement(inputEl)) {
        setErrors({
          ...errors,
          [fieldName]: undefined,
        })
        inputEl.checkValidity()
      }
    },
    [state, errors],
  ) as HandleChange<T>

  const onBlur = useCallback((ev: React.FocusEvent<HTMLInputElement>) => {
    blurred.current.push(ev.target.name)
    ev.currentTarget.checkValidity()
  }, [])

  const handleBadRequest = useCallback(
    (errors: ProblemDetails) => {
      displayUnknownError(
        errors.type == null ? undefined : translator(errors.type),
      )
    },
    [displayUnknownError, translator],
  )

  const handleServerValidationError = useCallback(
    (validationErrors: ValidationProblemDetails) => {
      if (validationErrors.errors == null) {
        handleBadRequest(validationErrors)
      } else {
        for (const [property, messages] of Object.entries(
          validationErrors.errors,
        )) {
          const inputEl = formRef?.current?.elements.namedItem(property)
          const errorMessage = translator(messages[0])

          if (isFormElement(inputEl)) {
            setErrors({
              ...errors,
              [property]: errorMessage,
            })
            inputEl.focus()
          } else {
            displayUnknownError(errorMessage)
          }
        }
      }
    },
    [errors, translator, displayUnknownError, handleBadRequest],
  )

  const handleUnknownError = useCallback(
    (err?: unknown) => {
      const error = toUnknownErrorMessage(t, err)
      displayUnknownError(error)
    },
    [displayUnknownError, t],
  )

  const abortController = useAbortController()

  const onSubmit = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      let isCancelling = false
      ev.preventDefault()
      ev.stopPropagation()
      hasSubmitted.current = true
      if (!ev.currentTarget.checkValidity()) {
        const inputEl = Array.from(ev.currentTarget.elements).find(
          e => isFormElement(e) && !e.validity.valid,
        ) as unknown as HTMLInputElement | HTMLSelectElement

        inputEl.focus()
        return
      }
      let processAfterSubmit: false | void = undefined
      try {
        setErrorMessage(undefined)
        setIsSubmitting(true)
        processAfterSubmit = await request(state, abortController.signal)
        setOriginalState(state)
      } catch (ex) {
        if (abortController.signal.aborted) {
          isCancelling = true
          return
        }
        if (isApiException(ex) && isProblemDetails(ex.error)) {
          if (isValidationProblemDetails(ex.error)) {
            handleServerValidationError(ex.error)
          } else if (ex.status === 400) {
            handleBadRequest(ex.error)
          } else {
            handleUnknownError(ex)
          }
        } else {
          handleUnknownError(ex)
        }
      } finally {
        if (!isCancelling && processAfterSubmit !== false) {
          setIsSubmitting(false)
        }
      }
    },
    [
      state,
      abortController.signal,
      handleBadRequest,
      handleServerValidationError,
      handleUnknownError,
      request,
      setErrorMessage,
    ],
  )

  useEffect(() => {
    if (shallowEqualObjects(state, originalState) === isDirty) {
      setIsDirty(!isDirty)
    }
  }, [state, isDirty, originalState])

  const [hasFocus, setHasFocus] = useState<string | undefined>(undefined)

  const removeFocus = (name: string) => {
    if (hasFocus === name) {
      setHasFocus(undefined)
    }
  }

  return {
    isSubmitting,
    setIsSubmitting,
    state,
    errors,
    isDirty,
    inputProps: {
      onChange,
      onBlur,
    },
    formProps: {
      ref: formRef,
      onSubmit,
      onInvalid,
      onReset,
    },
    addFocus: setHasFocus,
    removeFocus,
    hasFocus: (name: string) => name === hasFocus,
  }
}

type FormMethods<T> = {
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  state: T
  errors: Errors<T>
  isDirty: boolean
  inputProps: {
    onChange: HandleChange<T>
    onBlur: React.FocusEventHandler<HTMLInputElement>
  }
  formProps: {
    ref: React.RefObject<HTMLFormElement>
    onSubmit: (ev: React.FormEvent<HTMLFormElement>) => Promise<void>
    onInvalid: React.FormEventHandler<HTMLFormElement>
    onReset: React.FormEventHandler
  }
  addFocus: (name: string) => void
  removeFocus: (name: string) => void
  hasFocus: (name: string) => boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContext = React.createContext<FormMethods<any>>(
  undefined as unknown as FormMethods<unknown>,
)

export const FormProvider = <T extends unknown>({
  children,
  ...formProps
}: PropsWithChildren<FormMethods<T>>) => {
  return (
    <FormContext.Provider value={formProps}>
      <form
        css={`
          width: 100%;
        `}
        noValidate
        {...formProps.formProps}
      >
        {children}
      </form>
    </FormContext.Provider>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFormContext = <T extends { [i: string]: any }>() => {
  return useContext<FormMethods<T>>(FormContext)
}
